"use client"

import { Box, Text } from "@chakra-ui/react"
import { ButtonGroup, IconButton } from "@chakra-ui/react/button"
import { Pagination } from "@chakra-ui/react/pagination"
import { SegmentGroup } from "@chakra-ui/react/segment-group"
import { HStack } from "@chakra-ui/react/stack"
import { Stack } from "@chakra-ui/react/stack"
import { useRouter, useSearchParams } from "next/navigation"
import { Suspense } from "react"
import { ChevronLeftIcon, ChevronRightIcon } from "@/components/icons"

interface PaginationProps {
  totalPages: number
  pageSize?: number
  currentPage?: number
  totalPosts?: number
  onNavigateStart?: () => void
}

const PaginationBarInner = ({
  currentPage = 1,
  onNavigateStart,
  pageSize = 6,
  totalPages = 20,
  totalPosts = 120,
}: PaginationProps) => {
  const { push } = useRouter()
  const searchParams = useSearchParams()

  // Helper function to create URL with preserved filters
  const createUrlWithFilters = (page: number, perPage: number) => {
    const params = new URLSearchParams(searchParams?.toString() || "")
    params.set("page", page.toString())
    params.set("perPage", perPage.toString())
    return `/work?${params.toString()}`
  }
  return (
    <Stack
      bgColor="bg.paper"
      borderRadius="0"
      direction={{ base: "column", md: "row" }}
      gap={{ base: 0 }}
      justify="center"
      justifyContent="space-between"
      w="100%"
    >
      <Stack
        alignItems={{ base: "center", md: "center" }}
        borderBottom={{ base: "1px solid {colors.border}", md: "none" }}
        flexDirection={{ base: "row" }}
        mt={{ base: 0 }}
        pb={{ base: 0 }}
        px={{ base: 0 }}
        w={{ base: "100%", md: "auto" }}
      >
        <Pagination.Root
          count={totalPosts}
          onPageChange={(e) => {
            onNavigateStart?.()
            push(createUrlWithFilters(e.page, pageSize), {
              transitionTypes: [
                e.page > currentPage ? "work-page-forward" : "work-page-back",
              ],
            })
          }}
          page={currentPage}
          pageSize={pageSize}
        >
          <ButtonGroup gap={0} size="md" variant="ghost">
            <Box borderRight={"1px solid {colors.border}"}>
              <Pagination.PrevTrigger asChild>
                <IconButton rounded="0" width={9}>
                  <ChevronLeftIcon />
                </IconButton>
              </Pagination.PrevTrigger>
            </Box>
            <Pagination.Items
              render={(page) => (
                <IconButton
                  borderRight={"1px solid {colors.border}"}
                  rounded="0"
                  variant={{ base: "ghost", _selected: "subtle" }}
                  width={9}
                >
                  {page.value}
                </IconButton>
              )}
            />

            <Box borderRight={"1px solid {colors.border}"}>
              <Pagination.NextTrigger asChild>
                <IconButton rounded="0" width={9}>
                  <ChevronRightIcon />
                </IconButton>
              </Pagination.NextTrigger>
            </Box>
          </ButtonGroup>
        </Pagination.Root>
        <Text
          color={"fg.muted"}
          display={{ base: "none", md: "block" }}
          fontFamily={"mono"}
          fontSize={"xs"}
        >
          Page {currentPage} of {totalPages}
        </Text>
      </Stack>

      <HStack
        justifyContent={{ base: "flex-end", md: "flex-end" }}
        my={{ base: 0 }}
        px={{ base: 4, md: 0 }}
        w={{ base: "100%", md: "auto" }}
      >
        <Text color={"fg.muted"} fontFamily={"mono"} fontSize={{ base: "xs" }}>
          Articles per page
        </Text>

        <SegmentGroup.Root
          onValueChange={(e) => {
            onNavigateStart?.()
            push(createUrlWithFilters(1, parseInt(e.value || "6")), {
              transitionTypes: ["work-filter"],
            })
          }}
          rounded={"0"}
          value={pageSize !== undefined ? pageSize.toString() : "6"}
        >
          <SegmentGroup.Indicator bgColor="bg.paper" rounded={"0"} />
          <SegmentGroup.Items cursor={"pointer"} items={["6", "12", "16"]} />
        </SegmentGroup.Root>
      </HStack>
    </Stack>
  )
}

const PaginationBar = (props: PaginationProps) => (
  <Suspense fallback={null}>
    <PaginationBarInner {...props} />
  </Suspense>
)

export default PaginationBar
